import { Box, Tooltip } from '@mui/material';
import { TaskManagerItem } from '../../services/configApi/employees/employeeServices';
import { FC } from 'react';
import { ReactComponent as ArrowRight } from '../../assets/images/right.svg';
import MediumTypography from '../../components/formlib/MediumTypography';
import { useIntl } from 'react-intl';
import { getEmployeeDashboardColor } from '../../utils/utilities';
import { formatName } from '../../utils/nameUtils';

interface TaskManagerProps {
  tasks: TaskManagerItem[];
  handleNavigation: () => void;
}

const TaskManagerRow: FC<{ task: TaskManagerItem; index: number }> = ({
  task,
  index,
}) => {
  const rctl = useIntl();

  let handleName = formatName(
    task.firstName,
    task.middleName,
    task.lastName,
    '',
  );

  if (handleName === '') {
    handleName = '-';
  }

  return (
    <Box>
      {index !== 0 && <Box className={'defaultHorizontalLine'} />}
      <Box className={'flex__'}>
        <Box sx={{ flex: 0.3 }}>
          <Tooltip
            title={handleName ?? ''}
            followCursor
            arrow={true}
            sx={{ maxWidth: '150px' }}
          >
            <Box>
              <MediumTypography
                label={handleName ?? '-'}
                textColor="#2A4241"
                sxProps={{
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                }}
              />
            </Box>
          </Tooltip>
        </Box>
        <Box sx={{ flex: 0.5 }}>
          <Tooltip
            title={task.name ? task.name : ''}
            followCursor
            arrow={true}
            sx={{ maxWidth: '250px' }}
          >
            <Box>
              <MediumTypography
                label={task.name !== null ? task.name : '-'}
                textColor="#2A4241"
                sxProps={{
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                }}
              />
            </Box>
          </Tooltip>
        </Box>
        <MediumTypography
          sxProps={{ flex: 0.2 }}
          label={task.dueDate !== null ? task.dueDate : '-'}
        />
        <MediumTypography
          sxProps={{
            flex: 0.2,
            color: getEmployeeDashboardColor(
              task.status !== null ? task.status : '',
            ),
          }}
          label={
            task.status !== null ? rctl.formatMessage({ id: task.status }) : '-'
          }
        />
      </Box>
    </Box>
  );
};

const TaskManager: FC<TaskManagerProps> = ({ tasks, handleNavigation }) => {
  return (
    <Box className={'p-md'}>
      <Box className={'flex__ pt-xm'}>
        <MediumTypography
          sxProps={{ flexGrow: 1 }}
          className="mainText-lg"
          labelid="taskManagerText"
          defaultlabel="Task Manager"
        />
        <Box sx={{ cursor: 'pointer' }} onClick={handleNavigation}>
          <ArrowRight />
        </Box>
      </Box>

      <Box
        className={'pt-md'}
        sx={{
          overflowY: 'scroll',
          height: '195px',
        }}
      >
        {tasks.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              marginTop: '8%',
            }}
          >
            <MediumTypography
              sxProps={{ fontWeight: 'bold' }}
              labelid="noTasksAddedText"
              defaultlabel="No tasks added yet"
            />
          </Box>
        ) : (
          <Box className={'flex__ mb-sm'}>
            <MediumTypography
              sxProps={{
                color: 'var(--thom-mono-gray)',
                fontWeight: 'bold',
                flex: 0.3,
              }}
              labelid="Dashboard.ClientName"
              defaultlabel="Client Name"
            />
            <MediumTypography
              sxProps={{
                color: 'var(--thom-mono-gray)',
                fontWeight: 'bold',
                flex: 0.5,
              }}
              labelid="taskNameText"
              defaultlabel="Task Name"
            />
            <MediumTypography
              sxProps={{
                color: 'var(--thom-mono-gray)',
                fontWeight: 'bold',
                flex: 0.2,
              }}
              className="mr-none"
              labelid="dueDateText"
              defaultlabel="Due Date"
            />
            <Box sx={{ flex: 0.2 }}>
              <MediumTypography
                sxProps={{
                  color: 'var(--thom-mono-gray)',
                  fontWeight: 'bold',
                }}
                labelid="Dashboard.Status"
                defaultlabel="Status"
              />
            </Box>
          </Box>
        )}

        {tasks.map((task, index) => (
          <TaskManagerRow task={task} index={index} key={`task-${index + 1}`} />
        ))}
      </Box>
    </Box>
  );
};

export default TaskManager;
